import { useCallback, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { notification } from 'antd'
import { getAdminSettings } from 'enrollment-platform/utils/adminSettings'
import { useBulkUploadBillingProfilesMutation } from '../../api/client-billing-profile-api-client'
import { getAllBillingProfiles } from '../../api/client-billing-profile.api'
import type { BulkUploadResultDto } from '../../api/dto/client-billing-profile.dto'
import {
  BulkUploadParseError,
  buildUploadFile,
  downloadProfilesWorkbook,
  parseUploadWorkbook,
} from './bulkUpload.excel'
import { sortRowsErrorsFirst, type BulkUploadStep } from './bulkUpload.helpers'
import { useBulkUploadPlan } from './useBulkUploadPlan'
import { useBulkUploadRows } from './useBulkUploadRows'

const DEFAULT_PAGE_SIZE = 25

/** File selection, paging and submit flow behind the bulk upload modal. */
export const useBulkUpload = (onClose: () => void) => {
  const [step, setStep] = useState<BulkUploadStep>('select')
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [currentPage, setCurrentPage] = useState(1)
  const [result, setResult] = useState<BulkUploadResultDto | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const rows = useBulkUploadRows()
  const plan = useBulkUploadPlan({
    rows: rows.displayRows,
    enabled: step === 'preview',
  })

  const uploadMutation = useBulkUploadBillingProfilesMutation({
    onSuccess: (data) => {
      setResult(data)
      setStep('results')
      if (data.errors.length === 0) {
        notification.success({
          message: `Upload complete: ${data.created} created, ${data.updated} updated`,
        })
        return
      }
      notification.warning({
        message: `Upload completed with ${data.errors.length} ${
          data.errors.length === 1 ? 'error' : 'errors'
        }`,
      })
    },
    onError: () => {
      notification.error({ message: 'Upload failed. Please try again.' })
    },
  })

  const handleReset = () => {
    setStep('select')
    setSelectedFileName(null)
    rows.setRows([])
    setResult(null)
    setPageSize(DEFAULT_PAGE_SIZE)
    setCurrentPage(1)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.xlsx')) {
      notification.error({ message: 'Please select an .xlsx file' })
      return
    }

    setSelectedFileName(file.name)

    const reader = new FileReader()
    reader.onload = (loadEvent) => {
      try {
        const parsed = parseUploadWorkbook(
          loadEvent.target?.result as ArrayBuffer,
          rows.isKnownClientName
        )
        rows.setRows(sortRowsErrorsFirst(parsed))
        setStep('preview')
      } catch (error) {
        notification.error({
          message:
            error instanceof BulkUploadParseError
              ? error.message
              : 'Failed to parse Excel file',
        })
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleRemoveRow = useCallback(
    (rowNum: number) =>
      rows.setRows((curr) => {
        const next = curr.filter((row) => row.rowNum !== rowNum)
        // Clamp the page so the last page never renders empty after a delete.
        const maxPage = Math.max(1, Math.ceil(next.length / pageSize))
        setCurrentPage((page) => Math.min(page, maxPage))
        return next
      }),
    [pageSize, rows.setRows]
  )

  const handleUpload = () => {
    if (rows.rows.length === 0) return
    uploadMutation.mutate(buildUploadFile(rows.rows))
  }

  const handleDownloadCurrentValues = async () => {
    setIsExporting(true)
    try {
      const { showTestData } = getAdminSettings().finance
      downloadProfilesWorkbook(
        await getAllBillingProfiles({
          excludeTestClients: showTestData ? undefined : true,
        })
      )
    } catch {
      notification.error({
        message: 'Failed to download current billing profiles',
      })
    } finally {
      setIsExporting(false)
    }
  }

  return {
    step,
    selectedFileName,
    clients: rows.clients,
    displayRows: rows.displayRows,
    pagedRows: rows.displayRows.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    ),
    pageSize,
    currentPage,
    result,
    isExporting,
    isUploading: uploadMutation.isPending,
    fileInputRef,
    errorGroups: rows.errorGroups,
    errorIndex: rows.errorIndex,
    plansByRow: plan.plansByRow,
    planSummary: plan.planSummary,
    isPlanLoading: plan.isPlanLoading,
    isPlanError: plan.isPlanError,
    hasBlockingIssues:
      rows.rows.length === 0 ||
      rows.errorGroups.length > 0 ||
      // Nothing is planned yet, so no row can be vouched for.
      plan.isPlanLoading ||
      plan.isPlanError ||
      plan.planSummary.needsEmails > 0,
    setCurrentPage,
    handlePageSizeChange: (size: number) => {
      setPageSize(size)
      setCurrentPage(1)
    },
    handleReset,
    handleClose,
    handleFileSelect,
    handleClientFix: rows.handleClientFix,
    handleFieldChange: rows.handleFieldChange,
    handleRemoveRow,
    handleUpload,
    handleDownloadCurrentValues,
  }
}
